import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { generateEmbedUrl } from "client/Misc/youTube";
import {
	Dialog,
	IFrame,
	Toolbar
} from "client/Components";
import {
	HowToVideoDialogModel,
	HowToVideoModel
} from "client/Api/Workspace/LiveInformation/HowTos/howToLiveInformation";
import styles from "./HowToDialog.module.less";
import { Key } from "client/Misc/commonTypes";
import { youtubeAppIframePermissions } from "../../YouTube/YouTubeLiveTileContent";

export type HowToDialogProps = {
	onClose: () => void;
	allVideos: readonly HowToVideoModel[];
	video: HowToVideoDialogModel;
};

export const HowToDialog = (props: HowToDialogProps): ReactElement => {
	const {
		onClose,
		allVideos,
		video
	} = props;

	const [currentVideo, setCurrentVideo] = useState<HowToVideoModel>(video);
	const {title, description} = currentVideo;
	const embedUrl: string = generateEmbedUrl({
		videoId: currentVideo.youtubeId,
		forceSubtitles: true
	});

	useEffect(() => {
		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	});

	return (
		<Dialog
			isEnlargeable
			size="large"
			trackingId="how-to-dialog"
		>
			<Dialog.Header title={title} onClose={onClose} />
			<Dialog.Body>
				<div className={styles.content}>
					<div className={styles.description}>{description}</div>
					<IFrame
						className={styles.video}
						src={embedUrl}
						trackingId="how-to-video-iframe"
						permissions={youtubeAppIframePermissions}
					/>
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					{ canShowPrevious() && (
						<Toolbar.Button
							kind="primary"
							text={localize("_Previous")}
							onClick={showPrevious}
							trackingId="previous"
						/>
					)}
					{ canShowNext() && (
						<Toolbar.Button
							kind="primary"
							text={localize("_Next")}
							onClick={showNext}
							trackingId="next"
						/>
					)}
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);

	function currentIndex() {
		if (!currentVideo){
			return -1;
		}

		if (!allVideos || !allVideos.length) {
			return -1;
		}

		// Searching for the video by subject, it should be unique video id in front-end
		return allVideos.findIndex(v => v.subject === currentVideo.subject);
	};

	function canShowNext() {
		return currentIndex() < allVideos.length - 1;
	};

	function canShowPrevious() {
		return currentIndex() > 0;
	};

	function showNext() {
		if (!canShowNext()) {
			throw new Error("Cannot navigate to the next howto video");
		}

		setCurrentVideo(allVideos[currentIndex() + 1]);
	};

	function showPrevious() {
		if (!canShowPrevious()) {
			throw new Error("Cannot navigate to the previous howto video");
		}

		setCurrentVideo(allVideos[currentIndex() - 1]);
	};

	function handleKeyDown(event: KeyboardEvent) {
		const key: Key | null = tryResolveKeyboardKey(event);
		switch (key) {
			case Key.ArrowRight:
				showNext();
				break;
			case Key.ArrowLeft:
				showPrevious();
				break;
			default:
				// nothing to do
				break;
		};
	};
};

HowToDialog.displayName = "HowToDialog";